import React, { useEffect } from 'react';
import { Platform } from 'react-native';
import Animated, {
    interpolateColor,
    useAnimatedStyle,
    useSharedValue,
    withSequence,
    withTiming
} from 'react-native-reanimated';
import styled from 'styled-components/native';

import PhoneAppointmentImage from '~/assets/images/appointment-phone.svg';
import VideoAppointmentImage from '~/assets/images/appointment-video.svg';
import ClinicAppointmentImage from '~/assets/images/appointment-visit.svg';
import { RadioGroup } from '~/components/check';
import { RadioImageButton } from '~/components/check/radio-image-button';
import { Color } from '~/components/color';
import { Text } from '~/components/text';
import { FormattedMessage, useIntl } from '~/contexts/intl';
import { AppointmentContactType } from '~/types';
import { announceForAccessibility } from '~/utils/accessibility';

export type AppointmentContactTypeSelectProps = {
    contactTypes: readonly AppointmentContactType[];
    selectedContactType: AppointmentContactType | undefined;
    onSelectedContactTypeChange: (contactType: AppointmentContactType) => void;
    error?: boolean;
    flashNonce?: number;
};

export const AppointmentContactTypeSelectContainer = styled(Animated.View)`
    flex-direction: column;
    align-self: stretch;
    padding: 16px;
    border-width: 2px;
    border-color: transparent;
    border-radius: 8px;
`;

export function AppointmentContactTypeSelect(props: AppointmentContactTypeSelectProps) {
    const { contactTypes, selectedContactType, onSelectedContactTypeChange, error, flashNonce } = props;
    const { formatMessage } = useIntl();
    const types: AppointmentContactType[] = ['VISIT', 'VIDEO', 'PHONE'];
    const images = [ClinicAppointmentImage, VideoAppointmentImage, PhoneAppointmentImage];

    const flash = useSharedValue(0);
    useEffect(() => {
        flash.value = error
            ? withSequence(
                  withTiming(1, { duration: 150 }),
                  withTiming(0.3, { duration: 150 }),
                  withTiming(1, { duration: 150 })
              )
            : withTiming(0, { duration: 150 });
        // Re-run the flash on every failed tap; reanimated shared value `flash` is stable and intentionally omitted.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [error, flashNonce]);
    const animatedContainerStyle = useAnimatedStyle(() => ({
        borderColor: interpolateColor(flash.value, [0, 1], ['rgba(255, 66, 89, 0)', Color.CHERRY])
    }));

    const announcement = `${formatMessage('appointment.select-type')}, ${formatMessage('accessibility.required')}`;
    useEffect(() => {
        if (error && Platform.OS === 'ios') {
            announceForAccessibility(announcement);
        }
        // Re-announce on each failed tap (flashNonce); `error` is read but intentionally excluded so clearing the error does not re-announce.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [flashNonce]);

    return (
        <AppointmentContactTypeSelectContainer style={animatedContainerStyle}>
            <Text.SUBTITLE_1 after={error ? 4 : 16}>
                <FormattedMessage id="appointment.select-type" />
            </Text.SUBTITLE_1>
            {error ? (
                <Text.CAPTION
                    after={16}
                    accessibilityRole="alert"
                    accessibilityLiveRegion="assertive"
                    style={{ color: Color.CHERRY }}
                >
                    {formatMessage('appointment.select-type.required')}
                </Text.CAPTION>
            ) : null}
            <RadioGroup
                horizontal
                selectedIndex={types.findIndex(contactType => contactType === selectedContactType)}
                onChange={index => onSelectedContactTypeChange(types[index])}
            >
                {types.map((type, index) => {
                    const ImageComponent = images[index];
                    return (
                        <RadioImageButton
                            key={type}
                            imageLabel={formatMessage('appointment.type', { type })}
                            image={<ImageComponent height={60} width={60} />}
                            disabled={!contactTypes.includes(type)}
                        />
                    );
                })}
            </RadioGroup>
        </AppointmentContactTypeSelectContainer>
    );
}
